// Two repairs to City news text, shared by the Worker and the app.
//
// Dependency-free on purpose, the way `clamp.ts` is: the Worker repairs a
// Polizei Thema with `dehyphenate` before it is ever stored (`citynews.ts`),
// and the app runs both over what is already stored (`screens/Today.tsx`,
// `screens/City.tsx`), since rows written before the Worker learned this
// stay in `news` — and in a phone's cached copy — until they age out.

/** The word after a hyphen that makes it a suspended compound, not a line
 *  break: "zukunfts- und klimafit", "Küsten- und Serienraum", "pre- and
 *  post-war". The whole word: `und`, not `unde…`. */
const SUSPENDED = /^(?:und|oder|bzw|sowie|bis|noch|als|wie|beziehungsweise|statt|and|or|to|nor)$/i;

/**
 * Join a word the source broke at a line end and left as "-␠".
 *
 * The Versammlungsbehörde's Thema cell carries the hyphenation of whatever
 * the organiser pasted it from: "Kinder-Fahrrad-Demonstrat- ion",
 * "Grundr- echte", "Mierendor- ffinsel", "Planun- gs-Stopp". A lowercase
 * letter, a hyphen, one space and a lowercase word is a word cut in two, and
 * is joined with no hyphen. Before a capital it is a compound that kept its
 * hyphen and lost the line ("Pandemie- Diktat" → "Pandemie-Diktat").
 *
 * Not touched: a suspended compound (`SUSPENDED`), which is German's own
 * "-␠und" and the one place the same three characters are correct; a spaced
 * dash (" - ", a space before it); a hyphen after a capital or a digit
 * ("U- Bahn" is not a shape the sources produce, and "2025- 26" is a range).
 */
export function dehyphenate(text: string): string {
  return text.replace(
    /([a-zäöüß])- (?=([A-Za-zÄÖÜäöüß]+))/g,
    (whole, last: string, next: string) => {
      if (SUSPENDED.test(next)) return whole;
      return next[0] === next[0].toLowerCase() ? last : `${last}-`;
    },
  );
}

/**
 * A " · "-joined line with no separator beside an empty part.
 *
 * Rows the Worker wrote before it filtered an empty Versammlungsort carry
 * "07:15–08:00, · Aufzug" — a comma left over from joining time and place
 * when there was no place. This drops a comma or semicolon that stands
 * directly before a "·" or at the end, collapses two "·" with nothing
 * between them, and trims a "·" from either end.
 */
export function tidyJoins(text: string): string {
  return text
    .replace(/\s*[,;]\s*(?=·)/g, ' ')
    .replace(/\s*[,;]\s*$/, '')
    .replace(/·(?:\s*·)+/g, '·')
    .replace(/^\s*·\s*|\s*·\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Is `lang` German? A BCP-47 tag or a bare code ('de', 'de-DE'); the region's
 * `locale` is the intended argument. Nothing at all counts as German, which is
 * the answer for every row that existed before Boston: BVG, the Polizei,
 * berlin.de and the German newsletters.
 */
export const isGermanLang = (lang: string | null | undefined): boolean =>
  lang == null || lang === '' || /^de(?:$|[-_])/i.test(lang);

/**
 * Both repairs, in the order a stored row needs them — the first only for
 * German.
 *
 * `dehyphenate` reads "-␠" as a line break in a word the source hyphenated,
 * which is German typesetting. English prose has no such habit, and the one
 * place it could look like it ("long- term", a typo) is not a break to join.
 * The Worker applies it in `parsePolizei` alone, so no English source ever
 * reaches it there; the app calls this over every stored row, and passes the
 * current region's `locale` so a Concord Bridge headline is left as written.
 * `tidyJoins` mends a " · "-joined line and is the same in any language.
 */
export const tidyNewsText = (text: string, lang?: string | null): string =>
  tidyJoins(isGermanLang(lang) ? dehyphenate(text) : text);
